var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Modal dialog for browsing and selecting authority records.
 * Shows a searchable list with record heading and optionally code.
 * Works for all record types (vocabularies, authorities).
 * Uses "starts with" search mode for better prefix-based browsing.
 *
 * Two selection modes (TASK-064):
 *  - Single-select (default): row click → onSelect(id, heading, code) → modal closes.
 *  - Multi-select (multiSelect=true): leading checkbox column, local Set<id>
 *    selection, row click toggles, footer "Vybrat"/"Zrušit", confirm calls
 *    onSelectMany with the picked items.
 *
 * Three data sources:
 *  - Register (default, CZNA-1033): the register (rejstřík) opened at the typed text,
 *    `GET /records/index?pickable=true`, paged forward and back by keyset — the order of
 *    the Rejstříky screen, like the dropdown (see `utils/pickerRegister.ts`). A row is a
 *    heading; a variant row says which preferred heading picking it inserts.
 *  - Search (`reviewStatus` given): `GET /records` filtered by review state, which the
 *    register cannot filter on. Only the redirect mode of RecordReplace asks for it.
 *  - Static-list (staticOptions provided): no API call, list rendered directly
 *    from the supplied items, client-side substring filter on heading.
 *
 * Replaces VocabularyPickerModal — unified on records API.
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Modal, MultiColumnList, SearchField, Button, Checkbox, } from '@folio/stripes/components';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { fetchRecordIndex, fetchRecords } from '../../../api/recordService';
import { firstRowPerRecord, insertedHeading, insertsSuffix, typedAnchor, } from '../../../utils/pickerRegister';
var PAGE_SIZE = 50;
var INSERTS_SUFFIX_STYLE = { color: '#666' };
/**
 * CZNA-378: with the accumulating + caller-seeded `headingsById` this should be
 * unreachable — an id can only enter `selectedIds` from a rendered row or from
 * `initialSelectedIds`, and both populate the map. Dropping it is still the only
 * safe option (there is no heading to emit), but it deletes user data, so it must
 * not happen quietly. There is no toast channel in this component, hence console.
 */
/* eslint-disable no-console */
var warnDroppedSelection = function (id) {
    if (typeof console === 'undefined' || !console.warn)
        return;
    console.warn("[RecordPickerModal] dropping selected id ".concat(id, " \u2014 no heading resolved;")
        + ' pass initialSelectedHeadings for ids not on the loaded page.');
};
/* eslint-enable no-console */
var RecordPickerModal = function (props) {
    var _a, _b, _c;
    var open = props.open, onClose = props.onClose, recordType = props.recordType, alsoRecordTypes = props.alsoRecordTypes, staticOptions = props.staticOptions, _d = props.allowAddNew, allowAddNew = _d === void 0 ? false : _d, onAddNew = props.onAddNew, addNewLabel = props.addNewLabel, reviewStatus = props.reviewStatus, _f = props.initialSearchTerm, initialSearchTerm = _f === void 0 ? '' : _f;
    // The index endpoint takes the registers comma-separated in pickable mode.
    var registerTypes = __spreadArray([recordType], (alsoRecordTypes !== null && alsoRecordTypes !== void 0 ? alsoRecordTypes : []), true).join(',');
    var multiSelect = props.multiSelect === true;
    var intl = useIntl();
    var ky = useOkapiKy();
    var _g = useState(''), searchTerm = _g[0], setSearchTerm = _g[1];
    var footerAddNewLabel = addNewLabel
        ? addNewLabel(searchTerm)
        : intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.addNewTermButton' });
    var _h = useState(''), debouncedTerm = _h[0], setDebouncedTerm = _h[1];
    var _j = useState(0), offset = _j[0], setOffset = _j[1];
    // CZNA-1033: the register pages by keyset, not by offset. Each entry opens the page after
    // the one before it; empty = first page, opened at the typed text. `offset` is kept only
    // as the page position MultiColumnList displays.
    var _k = useState([]), nextCursors = _k[0], setNextCursors = _k[1];
    var debounceRef = useRef(undefined);
    // Multi-select local state — Set keyed by record id.
    var _l = useState(new Set()), selectedIds = _l[0], setSelectedIds = _l[1];
    useEffect(function () { return function () { return clearTimeout(debounceRef.current); }; }, []);
    // Reset state when modal reopens
    useEffect(function () {
        if (open) {
            setSearchTerm(initialSearchTerm);
            setDebouncedTerm(initialSearchTerm);
            setOffset(0);
            setNextCursors([]);
            if (multiSelect) {
                var initial = props.initialSelectedIds || [];
                setSelectedIds(new Set(initial));
            }
            else {
                setSelectedIds(new Set());
            }
        }
        // intentionally only watch `open` so initialSelectedIds changes mid-open don't override user's edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // API-backed modes — disabled when staticOptions is provided.
    var isStatic = Array.isArray(staticOptions);
    var browsesRegister = !isStatic && !reviewStatus;
    var searchQuery = useQuery(
    // `reviewStatus` belongs in the key: two callers with different filters share
    // this component, and without it the second would read the first's cached page.
    ['czna-record-picker-modal', recordType, debouncedTerm, offset, reviewStatus], function () { return fetchRecords(ky, {
        type: recordType,
        query: debouncedTerm || undefined,
        sortBy: 'headingValue',
        sortOrder: 'asc',
        limit: PAGE_SIZE,
        offset: offset,
        includeTotalCount: true,
        searchMode: 'STARTS_WITH',
        reviewStatus: reviewStatus,
    }); }, {
        enabled: open && !isStatic && !browsesRegister,
        keepPreviousData: true,
    });
    var cursor = nextCursors.length
        ? nextCursors[nextCursors.length - 1]
        : { anchor: typedAnchor(debouncedTerm) };
    // No keepPreviousData: a page opened at other text must not stand in for this one.
    var registerQuery = useQuery(['czna-record-picker-modal-register', registerTypes, cursor.anchor, cursor.anchorId], function () { return fetchRecordIndex(ky, {
        recordType: registerTypes,
        anchor: cursor.anchor,
        anchorId: cursor.anchorId,
        after: PAGE_SIZE,
        pickable: true,
    }); }, { enabled: open && browsesRegister });
    var _m = browsesRegister ? registerQuery : searchQuery, isFetching = _m.isFetching, isError = _m.isError;
    // An empty list after a failed read says nothing about whether the term exists, so
    // the footer must not offer to create the typed text then (CZNA-1033).
    var unread = isError && !isStatic;
    var registerEntries = (_a = registerQuery.data) === null || _a === void 0 ? void 0 : _a.entries;
    // Items rendered in the table — static (filtered client-side), register page or search page.
    var items = useMemo(function () {
        var _a;
        if (isStatic) {
            var term_1 = debouncedTerm.trim().toLowerCase();
            var filtered = term_1
                ? staticOptions.filter(function (o) { return o.headingValue.toLowerCase().startsWith(term_1); })
                : __spreadArray([], staticOptions, true);
            return filtered;
        }
        if (browsesRegister) {
            // One row per record: a record whose preferred heading and a variant both fall on the
            // page would otherwise be two rows with the same id. `code` stays undefined (unknown).
            return firstRowPerRecord(registerEntries || []).map(function (entry) { return ({
                id: entry.recordId,
                headingValue: insertedHeading(entry),
                found: entry.headingValue,
            }); });
        }
        return (((_a = searchQuery.data) === null || _a === void 0 ? void 0 : _a.items) || []).map(function (r) { return ({
            id: r.id, headingValue: r.headingValue, code: r.code,
        }); });
    }, [isStatic, browsesRegister, staticOptions, debouncedTerm, registerEntries, searchQuery.data]);
    var totalCount = isStatic ? items.length : (((_b = searchQuery.data) === null || _b === void 0 ? void 0 : _b.totalRecords) || 0);
    // Show code column only when at least one item has a non-null code
    var hasCode = useMemo(function () { return items.some(function (item) { return item.code != null; }); }, [items]);
    // Translate the record type(s) for the modal header
    var translatedType = __spreadArray([recordType], (alsoRecordTypes !== null && alsoRecordTypes !== void 0 ? alsoRecordTypes : []), true).map(function (type) { return intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type), defaultMessage: type }); })
        .join(', ');
    var modalLabel = intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.titleWithType' }, { type: translatedType });
    // Build columns dynamically — leading "select" column appears in multi mode.
    var visibleColumns = useMemo(function () {
        var cols = [];
        if (multiSelect)
            cols.push('_select');
        cols.push('headingValue');
        if (hasCode)
            cols.push('code');
        return cols;
    }, [hasCode, multiSelect]);
    var columnMapping = useMemo(function () {
        var mapping = {
            headingValue: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.column.heading' }),
        };
        if (hasCode) {
            mapping.code = intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.column.code' });
        }
        if (multiSelect) {
            mapping._select = intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.column.select' });
        }
        return mapping;
    }, [hasCode, intl, multiSelect]);
    var columnWidths = useMemo(function () {
        if (multiSelect) {
            return hasCode
                ? { _select: '8%', headingValue: '62%', code: '30%' }
                : { _select: '8%', headingValue: '92%' };
        }
        return hasCode ? { headingValue: '70%', code: '30%' } : { headingValue: '100%' };
    }, [hasCode, multiSelect]);
    var handleSearchChange = useCallback(function (e) {
        setSearchTerm(e.target.value);
        setOffset(0);
        setNextCursors([]);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () { return setDebouncedTerm(e.target.value); }, 300);
    }, []);
    var handleClear = useCallback(function () {
        setSearchTerm('');
        setDebouncedTerm('');
        setOffset(0);
        setNextCursors([]);
    }, []);
    /**
     * CZNA-1033: MultiColumnList asks for an offset; the register is paged by keyset. Forward
     * opens the next page just past the current page's last register row (value + id — the
     * row, not the deduplicated table row, or a heading could be skipped); back drops the
     * last cursor. Only the direction of the request matters: MultiColumnList asks for
     * `pagingOffset ± pageAmount`.
     */
    var handleRegisterPage = useCallback(function (_askAmount, index) {
        if (index < offset) {
            setNextCursors(function (prev) { return prev.slice(0, -1); });
            setOffset(function (prev) { return Math.max(0, prev - PAGE_SIZE); });
            return;
        }
        var last = registerEntries === null || registerEntries === void 0 ? void 0 : registerEntries[registerEntries.length - 1];
        if (!last)
            return;
        setNextCursors(function (prev) { return __spreadArray(__spreadArray([], prev, true), [{ anchor: last.headingValue, anchorId: last.headingId }], false); });
        setOffset(function (prev) { return prev + PAGE_SIZE; });
    }, [offset, registerEntries]);
    var toggleId = useCallback(function (id) {
        setSelectedIds(function (prev) {
            var next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    // Lookup heading by id across both static and dynamic sources, plus pre-known
    // initial selections. This comment used to describe behaviour the code did not
    // have: the map was rebuilt from `items` alone, i.e. the CURRENT page of at most
    // PAGE_SIZE rows, and `handleConfirm` silently drops any selected id it cannot
    // resolve. So a term picked on page 4 and then confirmed again from page 1 was
    // deleted from the caller's list without the user touching it — invisible while
    // every vocabulary fitted on one page, and reachable as of CZNA-378
    // (VOCABULARY_NAME_ADDITION derives ~227 terms).
    //
    // Two fixes, both needed: the map now ACCUMULATES across pages and searches for
    // as long as the modal stays open (a ref, so paging cannot forget), and it is
    // seeded from the caller's `initialSelectedHeadings` so ids selected in an
    // earlier session resolve before any page has loaded.
    var headingsRef = useRef(new Map());
    var initialSelectedHeadings = multiSelect
        ? props.initialSelectedHeadings
        : undefined;
    // Seeding happens HERE, in the memo, and not in an effect. An effect that
    // assigned `headingsRef.current = new Map(...)` looked equivalent and was not:
    // effects run after render, so this memo had already captured the PREVIOUS Map
    // object, and `handleConfirm` closes over that — the seed landed in a map
    // nothing read. It only appeared to work on a cold fetch that resolved after
    // mount (changing `items`, re-running the memo); on every reopen with a warm
    // React Query cache the selection was dropped exactly as before the fix.
    //
    // Nothing needs clearing between openings. This is a pure id → heading lookup;
    // only `selectedIds` decides what `handleConfirm` emits, so a leftover entry
    // from a previous opening can never add a value — it can only spare us a
    // lookup miss. That removes the reset, and with it the ordering hazard.
    //
    // Note for future edits: this returns the SAME Map object for the component's
    // lifetime, so anything keyed on `headingsById` in a dependency array will never
    // re-run. `handleConfirm` is fine (it also depends on `props`), but a new memo or
    // effect depending on it would silently never fire.
    var headingsById = useMemo(function () {
        var m = headingsRef.current;
        // Caller-supplied headings first, so a freshly loaded row still wins below.
        Object.entries(initialSelectedHeadings || {}).forEach(function (_a) {
            var id = _a[0], heading = _a[1];
            if (!m.has(id))
                m.set(id, heading);
        });
        items.forEach(function (it) {
            m.set(it.id, it.headingValue);
        });
        return m;
    }, [items, initialSelectedHeadings]);
    var handleRowClick = useCallback(function (_e, row) {
        var item = row;
        if (!(item === null || item === void 0 ? void 0 : item.id) || !(item === null || item === void 0 ? void 0 : item.headingValue))
            return;
        if (multiSelect) {
            toggleId(item.id);
        }
        else if (props.onSelect) {
            props.onSelect(item.id, item.headingValue, item.code);
        }
    }, [multiSelect, props, toggleId]);
    var handleConfirm = useCallback(function () {
        if (!multiSelect)
            return;
        var picked = [];
        selectedIds.forEach(function (id) {
            var heading = headingsById.get(id);
            if (heading) {
                picked.push({ id: id, heading: heading });
            }
            else {
                warnDroppedSelection(id);
            }
        });
        props.onSelectMany(picked);
    }, [multiSelect, selectedIds, headingsById, props]);
    // CZNA-1348 — the term the caller created from the footer joins the selection and the
    // round is confirmed, as a pick in the single-select mode closes it. Its heading goes
    // into the lookup first: the register page still shows the window from before the
    // create, so no rendered row could resolve the new id.
    var addedTerm = multiSelect ? props.addedTerm : undefined;
    var handledAddedTermRef = useRef(addedTerm);
    useEffect(function () {
        if (!addedTerm || addedTerm === handledAddedTermRef.current)
            return;
        handledAddedTermRef.current = addedTerm;
        headingsById.set(addedTerm.id, addedTerm.heading);
        var picked = [];
        new Set(__spreadArray(__spreadArray([], Array.from(selectedIds), true), [addedTerm.id], false)).forEach(function (id) {
            var heading = headingsById.get(id);
            if (heading)
                picked.push({ id: id, heading: heading });
            else
                warnDroppedSelection(id);
        });
        props.onSelectMany(picked);
    }, [addedTerm, selectedIds, headingsById, props]);
    var formatter = useMemo(function () { return ({
        // CZNA-1033: a variant row shows what was found and, set apart, what picking it inserts.
        headingValue: function (row) { return (row.found && row.found !== row.headingValue ? (React.createElement("span", null,
            row.found,
            ' ',
            React.createElement("span", { style: INSERTS_SUFFIX_STYLE }, insertsSuffix(row.headingValue, intl)))) : row.headingValue); },
        code: function (row) { return row.code || ''; },
        // CZNA-1351: the checkbox toggles the selection itself and keeps its click away from
        // the row. It used to be a no-op that relied on the click bubbling into MCL's row
        // handler — and it did bubble, but that handler calls `preventDefault()`, which makes
        // the browser undo the checkbox's own toggle once the event is over (canceled
        // activation). So the box never visibly changed, whatever the state said.
        // Stopping propagation here is also what keeps one click from toggling twice
        // (TASK-125: onChange + the row handler netted zero): the row never sees it.
        // Space on the focused box fires the same click, so the keyboard path is this one too.
        _select: function (row) { return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        React.createElement("span", { onClick: function (e) { return e.stopPropagation(); }, "data-test-picker-select": row.id },
            React.createElement(Checkbox, { checked: selectedIds.has(row.id), onChange: function () { return toggleId(row.id); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.column.select' }) }))); },
    }); }, [selectedIds, intl, toggleId]);
    return (React.createElement(Modal, { open: open, onClose: onClose, label: modalLabel, dismissible: true, size: "medium", footer: React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 16px' } },
            React.createElement("div", null, allowAddNew && footerAddNewLabel !== null && !unread && (React.createElement(Button, { onClick: function () { return onAddNew && onAddNew(searchTerm); }, "data-testid": "record-picker-add-new" }, footerAddNewLabel))),
            React.createElement("div", { style: { display: 'flex', gap: '8px' } }, multiSelect ? (React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: onClose }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.cancel' })),
                React.createElement(Button, { buttonStyle: "primary", onClick: handleConfirm }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.confirm' })))) : (React.createElement(Button, { onClick: onClose }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.close' }))))) },
        React.createElement("div", { style: { marginBottom: '16px' } },
            React.createElement(SearchField, { value: searchTerm, onChange: handleSearchChange, onClear: handleClear, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.search' }), autoFocus: true })),
        unread && (React.createElement("div", { role: "alert", style: { color: '#d0021b', padding: '8px', marginBottom: '12px' } }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.error' }))),
        React.createElement("div", { style: { height: '400px' } },
            React.createElement(MultiColumnList, __assign({ contentData: items, visibleColumns: visibleColumns, columnMapping: columnMapping, columnWidths: columnWidths, formatter: formatter, onRowClick: handleRowClick, loading: !isStatic && isFetching, isEmptyMessage: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.noResults' }), pagingType: "prev-next", pageAmount: PAGE_SIZE, pagingOffset: offset }, (browsesRegister ? {
                // The register has no total; it knows only whether there is more after this page.
                pagingCanGoNext: !!((_c = registerQuery.data) === null || _c === void 0 ? void 0 : _c.hasMoreAfter),
                pagingCanGoPrevious: nextCursors.length > 0,
                hidePageIndices: true,
                onNeedMoreData: handleRegisterPage,
            } : {
                totalCount: totalCount,
                onNeedMoreData: isStatic ? undefined : function (_askAmount, newOffset) { return setOffset(newOffset); },
            }), { autosize: true })))));
};
export default RecordPickerModal;
